import { createPortal } from 'react-dom';
import { TrendingDown, TrendingUp, X } from 'lucide-react';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { FilingPeriodForChart } from '@/utils/institutionalHoldersChartData';
import {
  institutionalHolderCardIsNegative,
  institutionalHolderCardMetricLabel,
} from '@/utils/institutionalHoldersChartData';
import type { InstitutionalHoldersChartDisplayMode } from '@/utils/institutionalHoldersChartDisplayMode';

const POPOVER_WIDTH_PX = 260;
const VIEWPORT_PAD = 8;

function clampPosition(anchor: DOMRect, height: number): { left: number; top: number } {
  let left = anchor.left + anchor.width / 2 - POPOVER_WIDTH_PX / 2;
  let top = anchor.bottom + VIEWPORT_PAD;
  left = Math.max(
    VIEWPORT_PAD,
    Math.min(left, window.innerWidth - POPOVER_WIDTH_PX - VIEWPORT_PAD)
  );
  if (top + height > window.innerHeight - VIEWPORT_PAD) {
    top = anchor.top - height - VIEWPORT_PAD;
  }
  top = Math.max(VIEWPORT_PAD, top);
  return { left, top };
}

export function InstitutionalHoldersQuarterPopover({
  period,
  displayMode = 'activity',
  anchorRect,
  onClose,
  onSelectInstitution,
  selectedPositionKey = null,
}: {
  period: FilingPeriodForChart;
  displayMode?: InstitutionalHoldersChartDisplayMode;
  anchorRect: DOMRect;
  onClose: () => void;
  onSelectInstitution: (positionKey: string, el: HTMLElement) => void;
  selectedPositionKey?: string | null;
}) {
  const isOwnership = displayMode === 'ownership';
  const listLabel = isOwnership ? 'holder' : 'position change';
  const ref = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState(() => clampPosition(anchorRect, 240));

  useLayoutEffect(() => {
    const h = ref.current?.offsetHeight ?? 240;
    setPosition(clampPosition(anchorRect, h));
  }, [anchorRect, period]);

  useEffect(() => {
    const onPointerDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (ref.current?.contains(t)) return;
      if (t instanceof Element && t.closest('[data-inst-overlay="holder-detail"]')) return;
      onClose();
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [onClose]);

  return createPortal(
    <div
      ref={ref}
      role="dialog"
      aria-label={`${period.quarterLabel} institutional ${isOwnership ? 'holders' : 'activity'}`}
      className="fixed z-[60] rounded-xl border border-slate-200 bg-white shadow-lg"
      style={{ left: position.left, top: position.top, width: POPOVER_WIDTH_PX }}
      onMouseDown={(e) => e.stopPropagation()}
    >
      <div className="flex items-start justify-between gap-2 border-b border-slate-100 px-3 py-2.5">
        <div>
          <p className="text-sm font-medium text-slate-900">{period.quarterLabel}</p>
          <p className="text-xs text-slate-500">
            {period.institutions.length} {listLabel}
            {period.institutions.length === 1 ? '' : 's'}
          </p>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="rounded-md p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
          aria-label="Close"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
      <ul className="institutional-holders-ribbon-scroll max-h-64 overflow-y-auto py-1">
        {period.institutions.map((inst) => {
          const isNegative = !isOwnership && institutionalHolderCardIsNegative(inst);
          const metricLabel = institutionalHolderCardMetricLabel(inst, displayMode);
          return (
            <li key={inst.positionKey}>
              <button
                type="button"
                className={`flex w-full items-center justify-between gap-2 px-3 py-2 text-left hover:bg-slate-50 ${
                  selectedPositionKey === inst.positionKey ? 'bg-slate-100' : ''
                }`}
                onClick={(e) => onSelectInstitution(inst.positionKey, e.currentTarget)}
              >
                <span className="min-w-0 truncate text-xs font-medium text-slate-800">
                  {inst.name}
                </span>
                <span
                  className={`flex shrink-0 items-center gap-1 text-xs ${
                    isOwnership ? 'text-slate-600' : isNegative ? 'text-rose-600' : 'text-emerald-700'
                  }`}
                >
                  {metricLabel}
                  {!isOwnership &&
                    (isNegative ? (
                      <TrendingDown className="h-3 w-3" />
                    ) : (
                      <TrendingUp className="h-3 w-3" />
                    ))}
                </span>
              </button>
            </li>
          );
        })}
      </ul>
    </div>,
    document.body
  );
}
